import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Phone, PencilRuler, Warehouse, Maximize2, Layers, Package, PackageOpen, Boxes, MoveHorizontal, LayoutGrid, DraftingCompass, Wrench, ShieldCheck, Cog, ClipboardCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { QuoteCTA } from "../components/CTA";
import { CAPABILITIES, CONTACT, HERO_IMAGE, HOW_WORK, NEEDS, PRODUCTS, SERVICES } from "../data/site";

const ICONS = { PencilRuler, Warehouse, Maximize2, Layers, Package, PackageOpen, Boxes, MoveHorizontal, LayoutGrid, DraftingCompass, Wrench, ShieldCheck, Cog, ClipboardCheck };
const EASE = [0.22, 1, 0.36, 1];

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 140]);
  return (
    <>
      <SEO title="CustoRacks | Customised Storage Solutions — Warehouse Racking & Shelving, Bangalore"
        description="Warehouse racking, shelving and space optimisation solutions designed around your storage requirements. Layout planning, supply and installation across Bangalore and Karnataka." />

      <section className="hero" data-testid="home-hero">
        <motion.div className="hero-bg" style={{ backgroundImage: `url(${HERO_IMAGE})`, y: heroY }} />
        <div className="container">
          <div className="hero-content">
            <motion.span className="hero-tag" style={{ display: "block" }} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15, ease: EASE }}>
              CustoRacks — Warehouse & Industrial Storage
            </motion.span>
            <h1>
              <span className="mask-line"><motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.25, ease: EASE }}>Customised Storage Solutions</motion.span></span>
              <span className="mask-line"><motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.42, ease: EASE }}>for <span className="accent">Every Requirement</span></motion.span></span>
            </h1>
            <motion.p className="hero-sub" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7, ease: EASE }}>
              Warehouse racking, shelving and space optimisation solutions designed around your requirements — from layout planning to supply and installation.
            </motion.p>
            <motion.div className="hero-btns" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.85, ease: EASE }}>
              <Link to="/contact" className="btn btn-orange" data-testid="hero-quote-btn">Get a Quote <ArrowRight size={16} /></Link>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="btn btn-whatsapp" data-testid="hero-whatsapp-btn"><MessageCircle size={16} /> WhatsApp Us</a>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="cap-strip" data-testid="capability-strip">
        <div className="container cap-grid">
          {CAPABILITIES.map((c, i) => {
            const Icon = ICONS[c.icon];
            return (
              <motion.div className="cap-item" key={c.name} data-testid={`capability-${c.icon.toLowerCase()}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: EASE }}>
                <Icon size={30} />
                <h3>{c.name}</h3>
                <p>{c.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="space-band" data-testid="space-band">
        <div className="container space-inner">
          <Reveal>
            <span className="sec-label">Customised, Not Off-the-Shelf</span>
            <h2>Designed Around Your Space</h2>
            <p>Share your warehouse dimensions, layout or photographs and we'll help you plan a suitable storage solution.</p>
          </Reveal>
          <Link to="/contact" className="btn btn-orange" data-testid="space-band-btn">Share Your Requirement <ArrowRight size={16} /></Link>
        </div>
      </section>

      <section className="section" data-testid="home-products">
        <div className="container">
          <Reveal className="section-head">
            <span className="sec-label">Products</span>
            <h2>Our Storage Solutions</h2>
            <p>Explore storage systems designed for different warehouse, industrial and commercial storage requirements.</p>
          </Reveal>
          <div className="grid-3">
            {PRODUCTS.slice(0, 6).map((p, i) => (
              <motion.div key={p.slug} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.09, ease: EASE }}>
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
          <div className="needs-cta">
            <Link to="/products" className="btn btn-navy" data-testid="view-all-products-btn">View All Products <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      <div className="marquee" aria-hidden="true" data-testid="marquee-strip">
        <div className="marquee-track">
          {[0, 1].map((i) => (
            <span key={i}>Selective Pallet Racking · Shelving Systems · Mezzanine Systems · Drive-In Racking · Cantilever Racking · Slotted Angle Racks · Mobile Shelving · Storage Pallets ·&nbsp;</span>
          ))}
        </div>
      </div>

      <section className="section section-grey" data-testid="how-work-section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="sec-label">How We Work</span>
            <h2>How We Deliver Your Storage Solution</h2>
            <p>A clear, five-step process from first conversation to completed installation.</p>
          </Reveal>
          <div className="process">
            {HOW_WORK.map((s, i) => (
              <motion.div className="process-step" key={s.num} data-testid={`how-step-${s.num}`}
                initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: EASE }}>
                <span className="step-num">{s.num}</span>
                <div>
                  <h3>{s.name}</h3>
                  <p>{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="solutions-section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="sec-label">Solution Finder</span>
            <h2>Not Sure Which Storage System You Need?</h2>
            <p>Tell us what you're storing. We'll help you identify a suitable storage solution.</p>
          </Reveal>
          <div className="grid-3">
            {NEEDS.map((n, i) => {
              const Icon = ICONS[n.icon];
              return (
                <motion.div key={n.need} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.09, ease: EASE }}>
                  <Link to={`/products/${n.slug}`} className="need-card" data-testid={`need-${n.slug}`}>
                    <Icon size={30} />
                    <h3>{n.need}</h3>
                    <div className="need-rec">
                      <small>Recommended</small>
                      <span>{n.rec} <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <div className="needs-cta">
            <Link to="/contact" className="btn btn-orange" data-testid="talk-to-custoracks-btn">Discuss Your Requirement <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      <section className="section section-grey" data-testid="services-section">
        <div className="container services-split">
          <Reveal><img src="/assets/products/pallet-racking.jpg" alt="Selective pallet racking planned, supplied and installed by CustoRacks" loading="lazy" /></Reveal>
          <div>
            <Reveal>
              <span className="sec-label">Services</span>
              <h2>From Design to Installation</h2>
              <p className="services-sub">Planning, supply, installation and ongoing support for your storage system.</p>
            </Reveal>
            <div className="svc-list">
              {SERVICES.map((s, i) => {
                const Icon = ICONS[s.icon];
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07, ease: EASE }}>
                    <Link to="/services" className="svc-row" data-testid={`service-${s.id}`}>
                      <Icon size={20} /><span>{s.name}</span><ArrowRight size={16} className="svc-arrow" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="section" data-testid="about-section">
        <div className="container" style={{ maxWidth: 720 }}>
          <Reveal>
            <span className="sec-label">About CustoRacks</span>
            <h2>Your Storage. Our Solution.</h2>
            <p style={{ color: "var(--text)", fontSize: 16, marginTop: 18 }}>CustoRacks provides customised storage solutions for warehouses, industrial facilities and commercial storage requirements — from racking and shelving to mezzanine systems, storage pallets and rack safety accessories.</p>
            <p style={{ color: "var(--text)", fontSize: 16, marginTop: 14 }}>Our approach is simple: understand the requirement, plan the right solution and deliver it professionally.</p>
            <div className="hero-btns">
              <Link to="/about" className="btn btn-navy" data-testid="home-about-btn">About CustoRacks <ArrowRight size={15} /></Link>
              <a href={CONTACT.phoneHref} className="btn btn-whatsapp" style={{ borderColor: "var(--navy)", color: "var(--navy)" }} data-testid="home-call-btn"><Phone size={15} /> {CONTACT.phoneDisplay}</a>
            </div>
          </Reveal>
        </div>
      </section>

      <QuoteCTA />
    </>
  );
}
